import type { GiftContentType } from '@/api/types/gift';
export { calculateEqualTierProgress } from '@/domain/cart/gift-exposure/progress';

export interface GiftDialogTerms {
  show?: boolean;
  label?: string;
  title?: string;
  content?: string;
  contentType?: GiftContentType;
}

export interface GiftRuleContent {
  title: string;
  content: string;
  contentType: Exclude<GiftContentType, ''>;
}

export interface GiftProgressTierInput {
  id: string | number;
  reached?: boolean;
  current?: boolean;
}

export type GiftProgressTierState = 'upcoming' | 'reached' | 'current';

export function resolveGiftProgressTierStates<T extends GiftProgressTierInput>(tiers: T[]) {
  return tiers.map((tier) => ({
    id: tier.id,
    state: (tier.current
      ? 'current'
      : tier.reached
        ? 'reached'
        : 'upcoming') as GiftProgressTierState,
  }));
}

export function normalizeGiftProgressPercent(value?: number) {
  if (value === undefined || !Number.isFinite(value)) return undefined;

  return Math.min(100, Math.max(0, value));
}

const ALLOWED_HTML_TAGS = new Set([
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'ul',
  'ol',
  'li',
  'a',
  'span',
  'div',
]);

const isSafeHref = (href: string) => /^(https?:|mailto:|tel:|#|\/(?!\/))/i.test(href.trim());

/**
 * Gift terms are managed content. Keep only a compact formatting allow-list so
 * an unsafe backend value cannot execute scripts or event handlers in the dialog.
 */
export function sanitizeGiftHtml(content: string) {
  const withoutDangerousBlocks = content.replace(
    /<(script|style|iframe|object|embed|svg|math)\b[^>]*>[\s\S]*?<\/\1\s*>/gi,
    '',
  );

  return withoutDangerousBlocks.replace(
    /<\/?([a-z0-9]+)([^>]*)>/gi,
    (tag, rawTag, rawAttributes) => {
      const tagName = rawTag.toLowerCase();
      if (!ALLOWED_HTML_TAGS.has(tagName)) return '';
      if (tag.startsWith('</')) return `</${tagName}>`;
      if (tagName !== 'a') return `<${tagName}>`;

      const hrefMatch = rawAttributes.match(/\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/i);
      const href = hrefMatch?.[1] || hrefMatch?.[2] || hrefMatch?.[3] || '';

      return isSafeHref(href)
        ? `<a href="${href.replace(/"/g, '&quot;')}" rel="noopener noreferrer">`
        : '<a>';
    },
  );
}

export function resolveGiftRuleContent(
  terms: GiftDialogTerms | undefined,
  fallback: GiftRuleContent,
): GiftRuleContent {
  return {
    title: terms?.title || fallback.title,
    content: terms?.content || fallback.content,
    contentType:
      terms?.contentType === 'html' ? 'html' : terms?.content ? 'text' : fallback.contentType,
  };
}
